import { http } from '@/api/client/httpClient';
import { ApiError } from '@/api/client/abpError';
import { createAbpCrudService } from '@/api/abpCrudService';
import type { PagedResult } from '@/api/types/abp';
import type {
  CreateProviderDto,
  ProviderAccountDto,
  ProviderAccountFeeDatatableInput,
  ProviderAccountFeeRowDto,
  ProviderAccountFormDto,
  ProviderAccountLogDatatableInput,
  ProviderAccountLogRowDto,
  ProviderAccountLookupDto,
  ProviderDatatableInput,
  ProviderDto,
  ProviderTypeItemDto,
  UpdateProviderDto,
} from '@/api/types/providers';

// Routes derived from ABP conventional-controller mapping of
// Finkit.Tenants.Admin.Providers.ProvidersAppService (controller base
// /api/app/providers; GetX -> GET, Create -> POST base, Update(id,input) ->
// PUT base/{id}, Delete(id) -> DELETE base/{id}, other -> POST; action name =
// method minus Async minus verb-prefix, kebab-cased):
//   GetListForDataTable(input)        -> GET    /api/app/providers/for-data-table
//   CreateAsync(input)                -> POST   /api/app/providers
//   UpdateAsync(id, input)            -> PUT    /api/app/providers/{id}
//   DeleteAsync(id)                   -> DELETE /api/app/providers/{id}
//   GetProviderTypesAsync()           -> GET    /api/app/providers/provider-types
//   GetAccountLookupAsync()           -> GET    /api/app/providers/account-lookup
//   GetAccountAsync(providerId)       -> GET    /api/app/providers/account?providerId=
//   CreateAccountAsync(input)         -> POST   /api/app/providers/account
//   UpdateAccountAsync(id, input)     -> PUT    /api/app/providers/account/{id}
//   GetAccountLogsForDataTable(input) -> GET    /api/app/providers/account-logs-for-data-table
//   GetAccountFeesForDataTable(input) -> GET    /api/app/providers/account-fees-for-data-table
const BASE = '/api/app/providers';

export const providersApi = {
  // Standard CRUD (list/create/update/remove) from the shared factory.
  ...createAbpCrudService<ProviderDto, CreateProviderDto, UpdateProviderDto, ProviderDatatableInput>(BASE),

  async getProviderTypes(): Promise<ProviderTypeItemDto[]> {
    const { data } = await http.get<ProviderTypeItemDto[]>(`${BASE}/provider-types`);
    return data;
  },

  async getAccountLookup(): Promise<ProviderAccountLookupDto> {
    const { data } = await http.get<ProviderAccountLookupDto>(`${BASE}/account-lookup`);
    return data;
  },

  async getAccount(providerId: string): Promise<ProviderAccountDto | null> {
    try {
      const { data } = await http.get<ProviderAccountDto | null>(`${BASE}/account/${providerId}`);
      // No account yet → create mode. The backend returns 200 with a null body,
      // but a 204/empty body deserialises to "" — normalise anything that isn't a
      // real account object (one with an id) to null so create mode is detected.
      if (!data || typeof data !== 'object' || !data.id) return null;
      return data;
    } catch (err) {
      // Some setups surface "no account" as a 404 instead of a null body.
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  async createAccount(input: ProviderAccountFormDto): Promise<string> {
    const { data } = await http.post<string>(`${BASE}/account`, input);
    return data;
  },

  async updateAccount(providerAccountId: string, input: ProviderAccountFormDto): Promise<void> {
    await http.put(`${BASE}/account/${providerAccountId}`, input);
  },

  async getAccountLogsForDataTable(
    input: ProviderAccountLogDatatableInput,
  ): Promise<PagedResult<ProviderAccountLogRowDto>> {
    const { data } = await http.get<PagedResult<ProviderAccountLogRowDto>>(
      `${BASE}/account-logs-for-data-table`,
      { params: input },
    );
    return data;
  },

  async getAccountFeesForDataTable(
    input: ProviderAccountFeeDatatableInput,
  ): Promise<PagedResult<ProviderAccountFeeRowDto>> {
    const { data } = await http.get<PagedResult<ProviderAccountFeeRowDto>>(
      `${BASE}/account-fees-for-data-table`,
      { params: input },
    );
    return data;
  },
};
